import { supabase } from '../supabase';
import { markInvoicesRemoved } from '../utils/invoice/removedInvoiceIds';
import { getInvoiceSnapshot } from '../utils/invoice/invoiceSnapshot';
import { fetchAllRows } from '../utils/async/fetchAllRows';
import { writeInvoiceEvent, INVOICE_EVENT_TYPES } from './invoiceEvents';
// Year-picker query lives in ./invoiceYears.js (500-line cap). Re-exported
// below so existing `import { getDistinctInvoiceYears } from invoiceService`
// call sites keep working.
import { getDistinctInvoiceYears, getPaidInvoiceYears, getPaidInvoicesCreatedInYear } from './invoiceYears';
// Estimate-link helpers moved to ./estimateInvoiceLink.js to keep this file
// under the 500-line cap. Imported here for internal use AND re-exported so
// existing call sites that import from invoiceService keep working without
// import-path churn.
// Void + void-notification moved to ./invoiceVoidOps.js (500-line cap) and
// re-exported below, so `invoiceService.voidInvoice(...)` call sites are
// unchanged. Pure move — no behavior change.
import { voidInvoice, sendVoidNotification } from './invoiceVoidOps';
// Scoped post-mutation-reconcile reads moved to ./invoiceReconcileReads.js
// (500-line cap) and re-exported below — same pattern as invoiceVoidOps.js.
import { getInvoicesByIds, getDraftInvoices, INVOICE_LIST_COLS } from './invoiceReconcileReads';
import {
  sumInvoiceTotalsForEstimate,
  areAllLinkedInvoicesPaidForEstimate,
  countActiveInvoicesForEstimate,
  syncEstimateStatusFromLinkedInvoices,
  getNextEstimateInvoiceSequence,
  fetchLinkedInvoicesForEstimate,
  estimatePaymentCapError,
  getEstimateInvoiceErrorKey,
  MAX_ESTIMATE_PAYMENT_INVOICES,
} from './estimateInvoiceLink';

const isMissingSnapshotColumnError = (error) => {
  const message = error?.message || '';
  return /snapshot/i.test(message) && /column|schema cache/i.test(message);
};

// Shared select shape for getInvoices/getInvoicesByIds/getDraftInvoices — the
// customers embed + reminders_paused_at every list/reconcile read needs.

export const getInvoices = async (tenantId = null, { year, page, pageSize = 50 } = {}) => {
  if (!tenantId) return { data: null, error: new Error('tenantId is required') };
  const cols = INVOICE_LIST_COLS;
  const applyFilters = (q) => {
    let query = q.eq('tenant_id', tenantId).is('deleted_at', null);
    if (year) {
      // Overlap-based filter: include any invoice whose period intersects the
      // year, not just invoices whose date_from falls inside it. Lifecycle
      // invoices that span Dec → Jan would otherwise be dropped on a
      // following-year query and their covered jobs would falsely reappear in
      // Ready (correctness bug — risk of double-billing).
      query = query.lte('date_from', `${year}-12-31`).gte('date_to', `${year}-01-01`);
    }
    return query;
  };

  // No `page` ⇒ go through fetchAllRows so PostgREST's silent 1000-row cap
  // cannot truncate a tenant-wide/multi-year read. `page` passed ⇒ unchanged.
  if (!page) {
    const res = await fetchAllRows((opts) =>
      applyFilters(supabase.from('invoices').select(cols, opts))
        .order('created_at', { ascending: false })
        .order('id', { ascending: true }),
    );
    return { data: res.data, error: res.error, count: res.count };
  }

  // bounded: paged path — the caller passes page/pageSize (`.range()` below).
  let query = applyFilters(supabase.from('invoices').select(cols, { count: 'exact' }));
  const from = (page - 1) * pageSize;
  query = query.range(from, from + pageSize - 1);
  const { data, error, count } = await query.order('created_at', { ascending: false });
  return { data, error, count };
};

/**
 * Atomically claim the next invoice number for a tenant.
 * RPC returns { number, year } and handles yearly reset automatically.
 */
export const getNextInvoiceNumber = async (tenantId) => {
  const currentYear = new Date().getFullYear();
  const { data, error } = await supabase.rpc('increment_invoice_number', { p_tenant_id: tenantId });
  // The SECURITY DEFINER RPC is the ONLY safe path — it atomically increments the
  // counter. The old direct-update fallback silently no-ops under the owner-only
  // company_settings RLS, which would hand back a DUPLICATE number. So on any RPC
  // failure we fail loudly rather than fabricate a number.
  if (error) throw error;
  if (data?.number == null) throw new Error('increment_invoice_number returned no number');
  return { number: data.number, year: data.year || currentYear };
};

export const formatInvoiceNumber = (num, year) => {
  if (num == null) return 'N/A';
  const y = year || new Date().getFullYear();
  return `INV-${y}-${String(num).padStart(4, '0')}`;
};

// Re-exports for the estimate-link helpers (imported above for internal use).
// Keeps the public API stable for existing import paths.
export {
  sumInvoiceTotalsForEstimate,
  areAllLinkedInvoicesPaidForEstimate,
  countActiveInvoicesForEstimate,
  syncEstimateStatusFromLinkedInvoices,
  getNextEstimateInvoiceSequence,
  fetchLinkedInvoicesForEstimate,
  getEstimateInvoiceErrorKey,
  MAX_ESTIMATE_PAYMENT_INVOICES,
  getDistinctInvoiceYears,
  getPaidInvoiceYears,
  getPaidInvoicesCreatedInYear,
  voidInvoice,
  sendVoidNotification,
  getInvoicesByIds,
  getDraftInvoices,
};

/** 23505 raised by the (tenant_id, idempotency_key) unique index (matched by index/column name). */
export function isIdempotencyKeyConflict(error) {
  if (!error || String(error.code || '') !== '23505') return false;
  // Index: invoices_tenant_idempotency_uq (20260509170000); details name the
  // idempotency_key column. Either is enough.
  return /idempotency_(key|uq)/i.test(`${error.message || ''} ${error.details || ''}`);
}

export const createInvoice = async (invoiceData, tenantId = null) => {
  if (!tenantId) return { data: null, error: new Error('tenantId is required') };
  const insertData = { ...invoiceData, tenant_id: tenantId };

  // Idempotency-key pre-flight (RD-BILL-007 B2). When the caller (bulk-create)
  // supplies a stable per-row key, a retry computes the same key and we
  // return the original row instead of inserting a duplicate. This is the
  // strongest dedup layer — runs BEFORE the estimate
  // pre-flight so it covers manual + estimate-derived invoices uniformly.
  if (insertData.idempotency_key) {
    const { data: existingByKey } = await supabase
      .from('invoices')
      // `total`: same reason as the estimate pre-flight below (duplicate row is summed).
      .select('id, source_estimate_id, total')
      .eq('tenant_id', insertData.tenant_id)
      .eq('idempotency_key', insertData.idempotency_key)
      .maybeSingle();
    if (existingByKey) {
      return { data: existingByKey, error: null, warning: null, duplicate: true, duplicateReason: 'idempotency' };
    }
  }

  // Estimate-linked: unique on (tenant, source_estimate_id, estimate_invoice_sequence)
  if (insertData.source_estimate_id) {
    if (insertData.estimate_invoice_sequence == null) {
      const { sequence, error: seqErr } = await getNextEstimateInvoiceSequence(
        insertData.source_estimate_id,
        tenantId,
      );
      if (seqErr) return { data: null, error: seqErr };
      // sequence === null means the allocator found NO free slot (every slot
      // 1..MAX is held by a live invoice). That is the cap, not an error — and
      // it MUST be caught here: `null > MAX` is false, so falling through would
      // insert a NULL estimate_invoice_sequence and corrupt the row.
      if (sequence == null) {
        return { data: null, error: estimatePaymentCapError() };
      }
      insertData.estimate_invoice_sequence = sequence;
    }
    // Belt-and-braces for an explicitly-passed sequence (the allocator can no
    // longer produce an out-of-range slot, but a caller can hand one in).
    const resolvedSequence = Number(insertData.estimate_invoice_sequence);
    if (!Number.isInteger(resolvedSequence) || resolvedSequence < 1 || resolvedSequence > MAX_ESTIMATE_PAYMENT_INVOICES) {
      return { data: null, error: estimatePaymentCapError() };
    }
    // Excludes voided so a freshly-claimed sequence after a void doesn't
    // false-positive on the previously-voided row sharing that sequence.
    // Pairs with the partial unique index (migration 20260508130000) which
    // also excludes voided.
    // SELECT SHAPE IS LOAD-BEARING: this row is returned as the `duplicate` and
    // folded into sumInvoiceTotalsForEstimate, which FILTERS on source_estimate_id
    // and sums each row's PRE-CREDIT total (invoicePreCreditTotal). Selecting
    // only 'id' dropped it from that sum, so a fully invoiced estimate was
    // written back as partially_invoiced with a success toast. The credit
    // columns (added credit-integrity, 2026-09-28) are load-bearing the same
    // way: omitting them makes invoicePreCreditTotal silently fall back to
    // this row's net `total`, undercounting a credited duplicate the same way
    // the original bug undercounted a missing row entirely.
    const { data: existingByEstimate } = await supabase
      .from('invoices')
      .select('id, total, source_estimate_id, estimate_invoice_sequence, account_credit_applied, credit_applied_concession, credit_applied_pre_tax, taxable_base, tax_amount, tax_rate')
      .eq('tenant_id', insertData.tenant_id)
      .eq('source_estimate_id', insertData.source_estimate_id)
      .eq('estimate_invoice_sequence', insertData.estimate_invoice_sequence)
      .neq('status', 'voided')
      .is('deleted_at', null)
      .maybeSingle();
    if (existingByEstimate) {
      return { data: existingByEstimate, error: null, warning: null, duplicate: true, duplicateReason: 'estimate' };
    }
  }

  // No (customer, period) pre-flight any more (KAN-484 Step B, RD-BILL-014):
  // the period unique index is dropped, so two invoices for the same customer
  // and dates are legitimate. "One visit, one invoice" is enforced by the
  // database link trigger (VISIT_ON_OTHER_INVOICE), not by dates.

  const { data, error } = await supabase.from('invoices').insert(insertData).select().single();

  // Double-tap race: a concurrent second insert with the same idempotency key
  // is refused by the (tenant_id, idempotency_key) unique index (23505). That is
  // SUCCESS — the first tap's row is the invoice. Re-read it and return it as a
  // duplicate (never an error toast, never a second invoice).
  if (error && insertData.idempotency_key && isIdempotencyKeyConflict(error)) {
    const { data: winner } = await supabase
      .from('invoices')
      .select('id, source_estimate_id, total')
      .eq('tenant_id', insertData.tenant_id)
      .eq('idempotency_key', insertData.idempotency_key)
      .maybeSingle();
    if (winner) return { data: winner, error: null, warning: null, duplicate: true, duplicateReason: 'idempotency' };
  }

  if (error && invoiceData?.snapshot && isMissingSnapshotColumnError(error)) {
    const { snapshot, ...legacyInvoiceData } = insertData;
    const fallbackResult = await supabase.from('invoices').insert(legacyInvoiceData).select().single();

    // 'invoice created' audit is written server-side by the invoices INSERT trigger (KAN-484).

    return {
      ...fallbackResult,
      warning: fallbackResult.error
        ? null
        : 'Invoice was created without an immutable snapshot. Apply the Supabase migration to enable saved invoice snapshots.',
    };
  }

  // 'invoice created' audit is written server-side by the invoices INSERT trigger (KAN-484).
  return { data, error, warning: null };
};

/**
 * The generic `invoice:edited` row for a direct column write. Two skip rules,
 * both deliberate:
 *   1. `status`-only writes — updateInvoiceStatus owns that event (a
 *      status_changed/paid row plus an `edited` row is two rows for one action).
 *   2. `suppressAudit` — the caller emits a MORE SPECIFIC event for this same
 *      user action, so the generic row would be the duplicate.
 * Extracted because the fallback (no-snapshot-column) path and the normal path
 * had drifting copies of the same decision.
 */
function emitInvoiceEditedIfNeeded({ tenantId, invoiceId, invoiceData, suppressAudit }) {
  if (suppressAudit) return;
  const raw = invoiceData && typeof invoiceData === 'object' ? invoiceData : {};
  const changed = Object.keys(raw).filter((k) => k !== 'tenant_id');
  if (changed.length === 0 || (changed.length === 1 && changed[0] === 'status')) return;
  void writeInvoiceEvent({
    tenantId,
    invoiceId,
    eventType: INVOICE_EVENT_TYPES.EDITED,
    payload: { changed_fields: changed },
  });
}

/**
 * Update an invoice's columns.
 *
 * @param {string} invoiceId
 * @param {object} invoiceData columns to write
 * @param {string|null} tenantId
 * @param {{ suppressAudit?: boolean }} [options] `suppressAudit: true` skips
 *   THIS helper's generic `invoice:edited` row. It exists for ONE reason: a
 *   caller that emits a more specific event for the same user action (no
 *   caller today: addJobsToInvoiceDraft keeps this generic `invoice:edited` row and the server
 *   trigger adds `invoice:jobs_changed`, KAN-711). One action ⇒ one audit row.
 *   OFF by default, so every other caller is unchanged. NEVER pass it without
 *   emitting a replacement event — an unaudited money write is worse than a
 *   generic one. It governs only this module's client-side write; a
 *   server-side (RPC/trigger) audit is unaffected by design.
 * @param {{ suppressAudit?: boolean, expectedTotal?: number|string, expectedRevision?: number|string }} [options]
 *   `expectedRevision` / `expectedTotal` (KAN-711): compare-and-swap on status='draft' and
 *   deleted_at IS NULL plus `revision = expectedRevision` (the server bumps `invoices.revision` on any
 *   money / scope change, so it catches a same-total edit) and/or `total = expectedTotal`.
 *   A miss returns `{ error: { code: 'TOTAL_CHANGED' } }`.
 */
export const updateInvoice = async (invoiceId, invoiceData, tenantId = null, options = {}) => {
  if (!tenantId) return { data: null, error: new Error('tenantId is required') };
  const suppressAudit = options?.suppressAudit === true;
  // KAN-711 compare-and-swap (opt-in): the write lands only while the row is still a live
  // draft carrying the total the caller reviewed. Zero rows -> error code TOTAL_CHANGED.
  const expectedTotal = options?.expectedTotal != null && Number.isFinite(Number(options.expectedTotal))
    ? Number(options.expectedTotal).toFixed(2)
    : null;
  const rev = options?.expectedRevision;
  const expectedRevision = rev != null && rev !== '' && Number.isInteger(Number(rev)) ? Number(rev) : null;
  const casRequested = expectedTotal != null || expectedRevision != null;
  const nextInvoiceData = { ...invoiceData, tenant_id: tenantId };
  const run = (payload) => {
    let q = supabase
      .from('invoices')
      .update(payload)
      .eq('id', invoiceId)
      .eq('tenant_id', tenantId);
    if (!casRequested) return q.select().single();
    q = q.eq('status', 'draft').is('deleted_at', null);
    if (expectedTotal != null) q = q.eq('total', expectedTotal);
    if (expectedRevision != null) q = q.eq('revision', expectedRevision);
    return q.select().maybeSingle().then((res) => (res.error || res.data
      ? res
      : { data: null, error: { code: 'TOTAL_CHANGED', message: 'TOTAL_CHANGED' } }));
  };
  const { data, error } = await run(nextInvoiceData);

  if (error && invoiceData?.snapshot && isMissingSnapshotColumnError(error)) {
    const { snapshot, ...legacyInvoiceData } = nextInvoiceData;
    const fallbackResult = await run(legacyInvoiceData);

    if (!fallbackResult.error) {
      emitInvoiceEditedIfNeeded({ tenantId, invoiceId, invoiceData, suppressAudit });
    }

    return {
      ...fallbackResult,
      warning: fallbackResult.error
        ? null
        : 'Invoice was updated without an immutable snapshot. Apply the Supabase migration to enable saved invoice snapshots.',
    };
  }

  if (!error) {
    emitInvoiceEditedIfNeeded({ tenantId, invoiceId, invoiceData, suppressAudit });
  }

  return { data, error, warning: null };
};

export const updateInvoiceStatus = async (invoiceId, status, tenantId = null) => {
  if (!tenantId) return { data: null, error: new Error('tenantId is required') };
  const { data, error } = await supabase.rpc('update_invoice_status_safe', {
    p_invoice_id: invoiceId,
    p_status: status,
  });
  // Both transitions this RPC supports now self-audit SERVER-SIDE inside
  // update_invoice_status_safe (draft->sent emits 'sent' with
  // delivery_method:'external' since 20260909120200; sent->paid emits
  // 'paid' with {to_status:'paid'} since 20260924170000, F3) — a client
  // emit here would double-log AND was the exact forgeable-by-any-role gap
  // the F3 security review found (invoice:paid/status_changed had no role
  // gate on the raw client allowlist). Do not re-add a client-side write
  // for this RPC's outcomes.
  return { data, error };
};

/**
 * Clone a voided pure-manual invoice into a new draft (RPC). Admin/billing only.
 * @param {string} sourceInvoiceId
 */
export const cloneVoidedManualInvoiceDraft = async (sourceInvoiceId) => {
  const { data, error } = await supabase.rpc('clone_voided_manual_invoice_draft', {
    p_source_invoice_id: sourceInvoiceId,
  });
  // The clone's 'invoice:created' audit is written server-side by the
  // invoices INSERT trigger (KAN-484, 20261013120200); the client pair is off
  // the audit allowlist, so a client emit here would be refused.
  return { data, error };
};

/**
 * Soft-delete an invoice. Routes through the `delete_invoice_safe`
 * SECURITY DEFINER RPC (migration 20260515120000) which sets
 * `deleted_at = now()` after enforcing JWT tenant + role + status checks.
 * RLS already hides soft-deleted rows from all reads, so the invoice
 * vanishes from the UI exactly like a hard delete would, but the data
 * survives for audit / future recovery.
 *
 * Audit ordering: the audit event is written AFTER the RPC confirms a
 * successful soft-delete. Previously this fired BEFORE the underlying
 * write — which produced ghost "Eliminada" entries in the activity log
 * whenever the write was rejected (the original RLS-via-UPDATE path was
 * stubbornly failing on Dev, which is why we moved to the RPC). The
 * enrich_audit_event_payload trigger runs SECURITY DEFINER, so it still
 * denormalizes the invoice context (number, customer, total) into the
 * audit row even though `deleted_at` is already set.
 */
export const deleteInvoice = async (invoiceId, tenantId = null) => {
  if (!tenantId) return { data: null, error: new Error('tenantId is required') };

  // Snapshot BEFORE the soft-delete RPC. The enrich trigger runs SECURITY
  // DEFINER and still fires post-delete, but capturing the fields here makes
  // the audit row self-contained (no join required months later).
  let invoiceSnapshot = null;
  try {
    const { data: row } = await supabase
      .from('invoices')
      .select('invoice_number, status, customer_id, customers!inner(full_name), snapshot, subtotal, tax_amount, total, line_items_count, sent_at, due_date')
      .eq('id', invoiceId)
      .eq('tenant_id', tenantId)
      .is('deleted_at', null)
      .maybeSingle();
    if (row) invoiceSnapshot = row;
  } catch (_) {
    // Fail-soft: audit emits with empty payload if snapshot fails.
  }

  // Routes through delete_invoice_safe RPC (SECURITY DEFINER) instead of a
  // raw UPDATE-with-RLS. Rationale + history in
  // 20260515120000_delete_invoice_safe_rpc.sql. The RPC validates JWT
  // tenant + role, enforces tenant isolation, refuses non-draft invoices,
  // and returns { id, source_estimate_id, already_deleted }.
  const { data, error } = await supabase.rpc('delete_invoice_safe', {
    p_invoice_id: invoiceId,
  });
  if (error) return { data: null, error };
  // KAN-1121: every reader (Cobrar rows, Ready-to-bill snapshot) drops this id at once - one place for every delete path.
  markInvoicesRemoved([invoiceId], (getInvoiceSnapshot(invoiceSnapshot)?.jobs || []).map((j) => j?.id));

  // Idempotent no-op when another tab already deleted it. No audit event,
  // no estimate re-sync — same behavior as the previous "WHERE deleted_at
  // IS NULL matches 0 rows" path.
  if (data?.already_deleted) {
    return { data: [], error: null };
  }

  // Audit event AFTER successful delete only — never write ghost entries.
  await writeInvoiceEvent({
    tenantId,
    invoiceId,
    eventType: INVOICE_EVENT_TYPES.DELETED,
    payload: invoiceSnapshot
      ? {
          invoice_number: invoiceSnapshot.invoice_number,
          status_at_action: invoiceSnapshot.status,
          customer_id: invoiceSnapshot.customer_id,
          customer_name: invoiceSnapshot.customers?.full_name ?? null,
          total: invoiceSnapshot.total,
          subtotal: invoiceSnapshot.subtotal,
          tax_amount: invoiceSnapshot.tax_amount,
          line_items_count: invoiceSnapshot.line_items_count,
        }
      : {},
  });

  if (data?.source_estimate_id) {
    await syncEstimateStatusFromLinkedInvoices(data.source_estimate_id, tenantId);
  }

  // Shape return value to match the previous .update().select() output so
  // callers checking `data[0].source_estimate_id` keep working.
  return {
    data: [{ id: data.id, source_estimate_id: data.source_estimate_id }],
    error: null,
  };
};

